/**
 * Which deployment the browser is currently looking at.
 *
 * Driven off the hostname rather than a build-time env var on purpose. Vite
 * bakes VITE_* values at build time (and Next bakes NEXT_PUBLIC_* the same
 * way), so an env-driven badge can silently lie after an env change until
 * someone remembers to redeploy — exactly the failure mode a "which
 * environment am I on" indicator exists to prevent. A hostname can't drift
 * from the thing it describes.
 */

export type DeployEnvironment = "production" | "staging" | "preview" | "local" | "unknown";

// Only these are production. Everything else gets badged — failing toward
// "tell me" rather than staying silent on a host we didn't anticipate.
const PRODUCTION_HOSTS = new Set(["app.hypebook.app", "school.hypebook.app", "admin.hypebook.app"]);

export function detectEnvironment(hostname: string): DeployEnvironment {
  const host = hostname.trim().toLowerCase();
  if (!host) return "unknown";
  if (PRODUCTION_HOSTS.has(host)) return "production";
  if (host === "localhost" || host === "127.0.0.1" || host === "[::1]" || host.endsWith(".localhost")) return "local";
  if (host === "hypebookdev.xyz" || host.endsWith(".hypebookdev.xyz")) return "staging";
  if (host.endsWith(".vercel.app")) return "preview";
  return "unknown";
}

/** Short label for the badge. null on production — nothing is shown there. */
export function environmentLabel(env: DeployEnvironment): string | null {
  switch (env) {
    case "production":
      return null;
    case "staging":
      return "STAGING";
    case "preview":
      return "PREVIEW";
    case "local":
      return "LOCAL";
    default:
      return "NON-PROD";
  }
}
